import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CalculatorOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import B2BLayout from '../../../layouts/b2b'
import fetchAPI from '../../../../lib/utils/fetch-api'
import {
  CarrierOption,
  LogisticsEnumCollections,
  VehicleData,
  VehicleOption,
  VehiclesFilters,
  VehiclesPagination,
  VehiclesSorter,
} from './interface'
import {
  DictionaryLinks,
  SelectWithAction,
  ToolbarActions,
  ToolbarFilters,
  VehiclesTableWrapper,
  VehiclesToolbar,
} from './styles'

const LOGISTICS_VEHICLE_COLLECTION_CODE = 'logistics_vehicle'
const LOGISTICS_CARRIER_COLLECTION_CODE = 'logistics_carrier'

const formatMoney = (value: number | null | undefined) => {
  const num = Number(value)
  return Number.isFinite(num)
    ? num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : ''
}

const formatNumber = (value: number | null | undefined) => {
  const num = Number(value)
  return Number.isFinite(num) ? num.toLocaleString('ru-RU') : ''
}

const LogisticsVehiclesList = () => {
  const [data, setData] = useState<VehicleData[]>([])
  const [vehicles, setVehicles] = useState<VehicleOption[]>([])
  const [carriers, setCarriers] = useState<CarrierOption[]>([])
  const [collections, setCollections] = useState<LogisticsEnumCollections>({})
  const [filters, setFilters] = useState<VehiclesFilters>({})
  const [sorter, setSorter] = useState<VehiclesSorter>({})
  const [pagination, setPagination] = useState<VehiclesPagination>({
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncingSchedex, setSyncingSchedex] = useState(false)
  const [recalculating, setRecalculating] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleData | null>(null)
  const [recalculateVehicle, setRecalculateVehicle] = useState<VehicleData | null>(null)
  const [enumModal, setEnumModal] = useState<{
    open: boolean
    collectionCode: string
    title: string
    fieldName: string
  } | null>(null)
  const [form] = Form.useForm()
  const [enumForm] = Form.useForm()
  const [recalculateForm] = Form.useForm()

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/logistics/vehicles',
        title: 'Машины',
      },
    ],
    [],
  )

  const fetchDictionaries = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/logistics/vehicles/enum-list')
      setVehicles(
        (response.vehicles || []).map((item: any) => ({
          label: item.value,
          value: item.id,
        })),
      )
      setCarriers(
        (response.carriers || []).map((item: any) => ({
          label: item.value,
          value: item.id,
        })),
      )
      setCollections(response.collections || {})
    } catch (error) {
      console.error('Ошибка загрузки справочников логистики:', error)
      message.error('Не удалось загрузить справочники логистики')
    }
  }, [])

  const fetchVehicles = useCallback(
    async (nextPagination = pagination, nextSorter = sorter, nextFilters = filters) => {
      try {
        setLoading(true)
        const params = new URLSearchParams()
        params.set('page', String(nextPagination.current || 1))
        params.set('pageSize', String(nextPagination.pageSize || 50))

        if (nextSorter.field && nextSorter.order) {
          params.set('sort_field', nextSorter.field)
          params.set('sort_order', nextSorter.order)
        }
        if (nextFilters.name) {
          params.set('name', nextFilters.name)
        }
        if (nextFilters.carrier_id) {
          params.set('carrier_id', String(nextFilters.carrier_id))
        }

        const response = await fetchAPI(`/api/logistics/vehicles/list?${params.toString()}`)
        setData(response.data || [])
        setPagination(prev => ({
          ...prev,
          ...(response.pagination || {}),
        }))
      } catch (error) {
        console.error('Ошибка загрузки машин:', error)
        message.error('Не удалось загрузить машины')
      } finally {
        setLoading(false)
      }
    },
    [filters, pagination, sorter],
  )

  useEffect(() => {
    fetchDictionaries()
  }, [fetchDictionaries])

  useEffect(() => {
    fetchVehicles()
  }, [])

  useEffect(() => {
    if (!selectedVehicle) {
      form.resetFields()
      return
    }

    form.setFieldsValue({
      vehicle_id: selectedVehicle.vehicle_id,
      carrier_id: selectedVehicle.carrier_id,
      driver_name: selectedVehicle.driver_name,
      cost_by_distance: selectedVehicle.cost_by_distance,
      vehicle_activation_cost: selectedVehicle.vehicle_activation_cost,
      cost_per_order: selectedVehicle.cost_per_order,
      max_weight: selectedVehicle.max_weight,
      max_volume: selectedVehicle.max_volume,
    })
  }, [form, selectedVehicle])

  const columns = useMemo<ColumnsType<VehicleData>>(
    () => [
      {
        title: 'Машина',
        dataIndex: 'vehicle_name',
        key: 'name',
        sorter: true,
        width: 180,
        render: (_, record) => record.vehicle_name || record.name || '',
      },
      {
        title: 'Перевозчик',
        dataIndex: 'carrier_name',
        key: 'carrier_name',
        sorter: true,
        width: 180,
      },
      {
        title: 'Водитель',
        dataIndex: 'driver_name',
        key: 'driver_name',
        sorter: true,
        width: 160,
      },
      {
        title: 'Стоимость за км',
        dataIndex: 'cost_by_distance',
        key: 'cost_by_distance',
        sorter: true,
        width: 120,
        render: value => formatMoney(value),
      },
      {
        title: 'Фикс. стоимость',
        dataIndex: 'vehicle_activation_cost',
        key: 'vehicle_activation_cost',
        sorter: true,
        width: 120,
        render: value => formatMoney(value),
      },
      {
        title: 'Стоимость за заказ',
        dataIndex: 'cost_per_order',
        key: 'cost_per_order',
        sorter: true,
        width: 130,
        render: value => formatMoney(value),
      },
      {
        title: 'Грузоподъемность, кг',
        dataIndex: 'max_weight',
        key: 'max_weight',
        sorter: true,
        width: 145,
        render: value => formatNumber(value),
      },
      {
        title: 'Площадь, м²',
        dataIndex: 'max_volume',
        key: 'max_volume',
        sorter: true,
        width: 120,
        render: value => formatNumber(value),
      },
    ],
    [],
  )

  const handleTableChange = (
    nextPagination: TablePaginationConfig,
    _filters: any,
    nextSorter: any,
  ) => {
    const normalizedPagination = {
      ...pagination,
      current: nextPagination.current || 1,
      pageSize: nextPagination.pageSize || 50,
    }
    const normalizedSorter = {
      field: nextSorter?.field,
      order: nextSorter?.order,
    }

    setPagination(normalizedPagination)
    setSorter(normalizedSorter)
    fetchVehicles(normalizedPagination, normalizedSorter, filters)
  }

  const applyFilters = (nextFilters: VehiclesFilters) => {
    const normalizedPagination = {
      ...pagination,
      current: 1,
    }
    setFilters(nextFilters)
    setPagination(normalizedPagination)
    fetchVehicles(normalizedPagination, sorter, nextFilters)
  }

  const resetFilters = () => {
    applyFilters({})
  }

  const handleSyncSchedex = async () => {
    try {
      setSyncingSchedex(true)
      await fetchAPI('/api/logistics/vehicles/sync-schedex', {
        method: 'POST',
      })
      await fetchDictionaries()
      await fetchVehicles()
      message.success('Данные из Шедекс обновлены')
    } catch (error) {
      console.error('Ошибка обновления данных из Шедекс:', error)
      message.error('Не удалось обновить данные из Шедекс')
    } finally {
      setSyncingSchedex(false)
    }
  }

  const openEnumModal = (collectionCode: string, title: string, fieldName: string) => {
    enumForm.resetFields()
    setEnumModal({
      open: true,
      collectionCode,
      title,
      fieldName,
    })
  }

  const handleCreateEnumValue = async () => {
    if (!enumModal) {
      return
    }

    try {
      const values: { value: string } = await enumForm.validateFields()
      const response = await fetchAPI('/api/logistics/vehicles/enum-value', {
        method: 'POST',
        body: JSON.stringify({
          collection_code: enumModal.collectionCode,
          value: values.value,
        }),
      })

      await fetchDictionaries()
      form.setFieldValue(enumModal.fieldName, response.data?.id)
      setEnumModal(null)
      enumForm.resetFields()
      message.success('Значение добавлено')
    } catch (error: any) {
      if (error?.errorFields) {
        return
      }
      console.error('Ошибка создания значения справочника:', error)
      message.error('Не удалось добавить значение')
    }
  }

  const handleSave = async () => {
    if (!selectedVehicle) {
      return
    }

    try {
      const values: Record<string, unknown> = await form.validateFields()
      setSaving(true)
      await fetchAPI(`/api/logistics/vehicles/${selectedVehicle.id}`, {
        method: 'POST',
        body: JSON.stringify({ vehicle: values }),
      })
      message.success('Машина обновлена')
      setSelectedVehicle(null)
      fetchVehicles()
    } catch (error: any) {
      if (error?.errorFields) {
        return
      }
      console.error('Ошибка сохранения машины:', error)
      message.error('Не удалось сохранить машину')
    } finally {
      setSaving(false)
    }
  }

  const handleRecalculateRouteSheets = async () => {
    if (!recalculateVehicle) {
      return
    }

    try {
      const values = await recalculateForm.validateFields()
      const [dateFrom, dateTo] = values.date_range

      setRecalculating(true)
      const response = await fetchAPI(
        `/api/logistics/vehicles/${recalculateVehicle.id}/recalculate-route-sheets`,
        {
          method: 'POST',
          body: JSON.stringify({
            date_from: dateFrom.format('YYYY-MM-DD'),
            date_to: dateTo.format('YYYY-MM-DD'),
          }),
        },
        true,
      )

      if (response.error) {
        message.error(response.error.message)
        return
      }

      setRecalculateVehicle(null)
      recalculateForm.resetFields()
      Modal.info({
        title: 'Пересчёт завершён',
        content: (
          <Space direction="vertical" size={4}>
            <Typography.Text>Обновлено: {response.updated_count || 0}</Typography.Text>
            <Typography.Text>
              Уже имели текущую ставку: {response.unchanged_count || 0}
            </Typography.Text>
            <Typography.Text>
              Пропущено с ручной оплатой: {response.skipped_manual_count || 0}
            </Typography.Text>
          </Space>
        ),
      })
    } catch (error: any) {
      if (error?.errorFields) {
        return
      }
      console.error('Ошибка пересчёта маршрутных листов:', error)
      message.error('Не удалось пересчитать маршрутные листы')
    } finally {
      setRecalculating(false)
    }
  }

  const handleOpenRecalculation = () => {
    if (!selectedVehicle) {
      return
    }

    const formVehicleId = Number(form.getFieldValue('vehicle_id'))
    const storedVehicleId = Number(selectedVehicle.vehicle_id)
    const formCost = form.getFieldValue('cost_by_distance')
    const normalizedFormCost = formCost === null || formCost === undefined ? null : Number(formCost)
    const storedCost =
      selectedVehicle.cost_by_distance === null || selectedVehicle.cost_by_distance === undefined
        ? null
        : Number(selectedVehicle.cost_by_distance)

    if (formVehicleId !== storedVehicleId || normalizedFormCost !== storedCost) {
      message.warning('Сначала сохраните изменения машины, затем откройте её снова для пересчёта')
      return
    }

    recalculateForm.resetFields()
    setRecalculateVehicle(selectedVehicle)
  }

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle="Машины">
      <DictionaryLinks>
        <Typography.Text type="secondary">Справочники:</Typography.Text>
        {collections.vehicles ? (
          <Typography.Link
            href={`/admin/enum-collection/${collections.vehicles}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Машины
          </Typography.Link>
        ) : (
          <Typography.Text type="secondary">Машины</Typography.Text>
        )}
        {collections.carriers ? (
          <Typography.Link
            href={`/admin/enum-collection/${collections.carriers}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Перевозчики
          </Typography.Link>
        ) : (
          <Typography.Text type="secondary">Перевозчики</Typography.Text>
        )}
      </DictionaryLinks>

      <VehiclesToolbar>
        <ToolbarFilters>
          <Input.Search
            allowClear
            placeholder="Название машины"
            style={{ width: 260 }}
            value={filters.name}
            onChange={event => setFilters(prev => ({ ...prev, name: event.target.value }))}
            onSearch={value => applyFilters({ ...filters, name: value || undefined })}
          />
          <Select
            allowClear
            showSearch
            placeholder="Перевозчик"
            style={{ width: 260 }}
            options={carriers}
            value={filters.carrier_id}
            optionFilterProp="label"
            onChange={value => applyFilters({ ...filters, carrier_id: value || undefined })}
          />
          <Button onClick={resetFilters}>Сбросить</Button>
        </ToolbarFilters>
        <ToolbarActions>
          <Tooltip title="Загрузить актуальные машины из Шедекс и обновить справочник машин и таблицу logistics_vehicles">
            <Button
              type="primary"
              icon={<SyncOutlined spin={syncingSchedex} />}
              loading={syncingSchedex}
              onClick={handleSyncSchedex}
            >
              Обновить данные из Шедекс
            </Button>
          </Tooltip>
        </ToolbarActions>
      </VehiclesToolbar>

      <VehiclesTableWrapper>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          onRow={record => ({
            onClick: () => setSelectedVehicle(record),
          })}
          size="small"
          bordered
        />
      </VehiclesTableWrapper>

      <Modal
        title="Редактирование машины"
        open={!!selectedVehicle}
        onCancel={() => setSelectedVehicle(null)}
        onOk={handleSave}
        okButtonProps={{ loading: saving }}
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Машина" required>
            <SelectWithAction>
              <Form.Item
                name="vehicle_id"
                noStyle
                rules={[{ required: true, message: 'Укажите машину' }]}
              >
                <Select
                  showSearch
                  placeholder="Выберите машину"
                  options={vehicles}
                  optionFilterProp="label"
                />
              </Form.Item>
              <Tooltip title="Добавить машину в справочник">
                <Button
                  icon={<PlusOutlined />}
                  onClick={() =>
                    openEnumModal(LOGISTICS_VEHICLE_COLLECTION_CODE, 'Новая машина', 'vehicle_id')
                  }
                />
              </Tooltip>
            </SelectWithAction>
          </Form.Item>
          <Form.Item label="Перевозчик">
            <SelectWithAction>
              <Form.Item name="carrier_id" noStyle>
                <Select
                  allowClear
                  showSearch
                  placeholder="Выберите перевозчика"
                  options={carriers}
                  optionFilterProp="label"
                />
              </Form.Item>
              <Tooltip title="Добавить перевозчика в справочник">
                <Button
                  icon={<PlusOutlined />}
                  onClick={() =>
                    openEnumModal(
                      LOGISTICS_CARRIER_COLLECTION_CODE,
                      'Новый перевозчик',
                      'carrier_id',
                    )
                  }
                />
              </Tooltip>
            </SelectWithAction>
          </Form.Item>
          <Form.Item name="driver_name" label="Водитель">
            <Input />
          </Form.Item>
          <Space size={12} style={{ width: '100%' }}>
            <Form.Item name="cost_by_distance" label="Стоимость за км">
              <InputNumber min={0} precision={2} style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="vehicle_activation_cost" label="Фикс. стоимость">
              <InputNumber min={0} precision={2} style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="cost_per_order" label="Стоимость за заказ">
              <InputNumber min={0} precision={2} style={{ width: 160 }} />
            </Form.Item>
          </Space>
          <Space size={12} style={{ width: '100%' }}>
            <Form.Item name="max_weight" label="Грузоподъемность, кг">
              <InputNumber min={0} precision={2} style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="max_volume" label="Площадь, м²">
              <InputNumber min={0} precision={2} style={{ width: 160 }} />
            </Form.Item>
          </Space>
        </Form>
        <Space style={{ width: '100%', justifyContent: 'flex-start' }}>
          <Tooltip
            title={
              selectedVehicle?.cost_by_distance === null ||
              selectedVehicle?.cost_by_distance === undefined
                ? 'Сначала укажите и сохраните стоимость за км'
                : 'Применить текущую стоимость за км к маршрутным листам за выбранный период'
            }
          >
            <span>
              <Button
                type="primary"
                icon={<CalculatorOutlined />}
                disabled={
                  !selectedVehicle?.vehicle_id ||
                  selectedVehicle?.cost_by_distance === null ||
                  selectedVehicle?.cost_by_distance === undefined
                }
                onClick={handleOpenRecalculation}
              >
                Пересчитать оплату
              </Button>
            </span>
          </Tooltip>
        </Space>
      </Modal>

      <Modal
        title="Пересчёт оплаты перевозчику"
        open={!!recalculateVehicle}
        onCancel={() => {
          setRecalculateVehicle(null)
          recalculateForm.resetFields()
        }}
        onOk={handleRecalculateRouteSheets}
        okText="Пересчитать"
        cancelText="Отмена"
        confirmLoading={recalculating}
        cancelButtonProps={{ disabled: recalculating }}
        closable={!recalculating}
        maskClosable={!recalculating}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text>
            Машина: <b>{recalculateVehicle?.vehicle_name || recalculateVehicle?.name}</b>
          </Typography.Text>
          <Typography.Text>
            Текущая стоимость за км: <b>{formatMoney(recalculateVehicle?.cost_by_distance)} ₽</b>
          </Typography.Text>
          <Form form={recalculateForm} layout="vertical">
            <Form.Item
              name="date_range"
              label="Период маршрутных листов"
              rules={[{ required: true, message: 'Укажите период пересчёта' }]}
            >
              <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Form>
          <Typography.Text type="secondary">
            Для маршрутных листов за выбранный период сохранённая ставка будет заменена текущей.
            Листы с вручную заданной оплатой перевозчику будут пропущены.
          </Typography.Text>
        </Space>
      </Modal>

      <Modal
        title={enumModal?.title}
        open={!!enumModal?.open}
        onCancel={() => setEnumModal(null)}
        onOk={handleCreateEnumValue}
        destroyOnClose
      >
        <Form form={enumForm} layout="vertical">
          <Form.Item
            name="value"
            label="Название"
            rules={[{ required: true, message: 'Укажите название' }]}
          >
            <Input autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </B2BLayout>
  )
}

export default LogisticsVehiclesList
